import { request } from './client';

export interface SoraVideoContentJson {
  id?: string;
  status?: string;
  video_url?: string | null;
  videoUrl?: string | null;
  [key: string]: unknown;
}

export interface SoraVideoContentResult {
  blob?: Blob;
  filename?: string;
  videoUrl?: string;
}

export interface SoraRemixResponse {
  id?: string;
  status?: string;
  request_id?: string;
  [key: string]: unknown;
}

const parseFilenameFromDisposition = (value: string | null | undefined, fallback: string): string => {
  if (!value) return fallback;
  try {
    const decoded = value
      .split(';')
      .map((part) => part.trim())
      .filter((part) => part.length > 0);

    for (const part of decoded) {
      if (part.toLowerCase().startsWith('filename*=')) {
        const matched = part.substring(part.indexOf('=') + 1).trim();
        const utf8Prefix = "utf-8''";
        if (matched.toLowerCase().startsWith(utf8Prefix)) {
          const encodedName = matched.substring(utf8Prefix.length);
          return decodeURIComponent(encodedName);
        }
        return matched.replace(/^["']|["']$/g, '');
      }
      if (part.toLowerCase().startsWith('filename=')) {
        const rawName = part.substring(part.indexOf('=') + 1).trim();
        return rawName.replace(/^["']|["']$/g, '');
      }
    }
  } catch {
    // 忽略解析失败，回退默认文件名
  }
  return fallback;
};

const ensureSoraVideoId = (input: string): string => {
  const trimmed = input.trim();
  if (!trimmed) {
    throw new Error('缺少 Sora 视频 ID');
  }
  return trimmed;
};

export const fetchSoraVideoContent = async (videoId: string): Promise<SoraVideoContentResult> => {
  const normalizedId = ensureSoraVideoId(videoId);
  const response = await request<Response>({
    endpoint: `/v1/videos/${encodeURIComponent(normalizedId)}/content`,
    method: 'GET',
    responseType: 'response',
    includeAuth: true,
  });

  const contentType = (response.headers.get('content-type') || '').toLowerCase();

  if (contentType.includes('application/json')) {
    try {
      const data = (await response.json()) as SoraVideoContentJson;
      const directUrl =
        (typeof data.video_url === 'string' && data.video_url.trim()) ||
        (typeof data.videoUrl === 'string' && data.videoUrl.trim());
      if (directUrl) {
        return { videoUrl: directUrl };
      }
    } catch {
      // 无法解析为 JSON，继续尝试按二进制处理
    }
  }

  const blob = await response.blob();
  const fallbackName = `sora-${normalizedId}.mp4`;
  const filename = parseFilenameFromDisposition(response.headers.get('content-disposition'), fallbackName);
  return { blob, filename };
};

export const remixSoraVideo = async (videoId: string, prompt: string): Promise<SoraRemixResponse> => {
  const normalizedId = ensureSoraVideoId(videoId);
  const trimmedPrompt = prompt.trim();
  if (!trimmedPrompt) {
    throw new Error('缺少编辑提示词');
  }
  return request<SoraRemixResponse>({
    endpoint: `/v1/videos/${encodeURIComponent(normalizedId)}/remix`,
    method: 'POST',
    body: { prompt: trimmedPrompt },
    includeAuth: true,
    contentType: 'application/json',
  });
};

